import type { BoxShadowModifierValue } from "schemas/generated/fileModifiers";

import React, { useMemo } from "react";

import { useSelectedElement } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import { ChevronDown } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";

const SHADOW_OPTIONS = [
  {
    value: "none",
    label: "None",
    displayValue: "None",
  },
  {
    value: "2xs",
    label: "2XS",
    displayValue: "2XS",
  },
  {
    value: "xs",
    label: "XS",
    displayValue: "XS",
  },
  {
    value: "sm",
    label: "Small",
    displayValue: "Small",
  },
  {
    value: "md",
    label: "Medium",
    displayValue: "Medium",
  },
  {
    value: "lg",
    label: "Large",
    displayValue: "Large",
  },
  {
    value: "xl",
    label: "Extra Large",
    displayValue: "Extra Large",
  },
  {
    value: "2xl",
    label: "2XL",
    displayValue: "2XL",
  },
] as const;

interface ShadowModifierProps {
  onChange: (modifiers: BoxShadowModifierValue[]) => void;
}

const ShadowModifierComponent: React.FC<ShadowModifierProps> = ({
  onChange,
}) => {
  const selectedElement = useSelectedElement();

  const extractedValue = useMemo(() => {
    if (!selectedElement) {
      return "none";
    }

    // NOTE (Kevin, 2025-07-29): We cannot use computedStyles.boxShadow because the end result with all the px values
    // and color values would be too difficult to parse and convert back into a Tailwind shadow type.
    // Instead, we parse the className to find Tailwind shadow classes. If no matching class is found
    // but there exists a computed boxShadow value, we show "Custom" in the UI (but don't add it as
    // a selectable option). When a user selects a real shadow option from "Custom" state, it works
    // normally. When they select "None" from "Custom" state, it removes the shadow.
    const className = selectedElement.props?.className;
    if (className && typeof className === "string") {
      const classes = className.split(" ").filter(Boolean);

      for (const cls of classes) {
        if (cls === "shadow") {
          return "md";
        }
        if (cls.startsWith("shadow-")) {
          const shadowValue = cls.replace("shadow-", "");
          if (SHADOW_OPTIONS.some((option) => option.value === shadowValue)) {
            return shadowValue;
          }
        }
      }
    }

    // Check if there's a computed boxShadow value that doesn't match our classes
    const computedShadow = selectedElement.styleOverrides?.boxShadow;
    if (
      computedShadow &&
      computedShadow !== "none" &&
      computedShadow !== "0 0 #0000"
    ) {
      return "custom";
    }

    return "none";
  }, [selectedElement]);

  const [currentValue, setCurrentValue] = useOverridableState(
    extractedValue,
    useDebouncedCallback((shadowValue: string) => {
      if (SHADOW_OPTIONS.some((option) => option.value === shadowValue)) {
        onChange([
          {
            type: "boxShadow" as const,
            value: shadowValue as BoxShadowModifierValue["value"],
          },
        ]);
      }
    }, 300),
  );

  const currentOption = SHADOW_OPTIONS.find(
    (option) => option.value === currentValue,
  );

  const displayTitle =
    currentValue === "custom"
      ? "Custom"
      : currentOption?.displayValue ?? "None";
  const isPlaceholder = currentValue === null || currentValue === "none";

  const shadowOptions = SHADOW_OPTIONS.map((shadow) => ({
    ...shadow,
    value: shadow.value,
    label: (
      <MenuItem
        variant="default"
        size="sm"
        selected={currentValue === shadow.value}
      >
        {shadow.label}
      </MenuItem>
    ),
  }));

  return (
    <div className="shadow-modifier">
      <Combobox.Root
        options={shadowOptions}
        value={currentValue === "custom" ? "none" : currentValue ?? "none"}
        onChange={(value) => setCurrentValue(value)}
      >
        <Combobox.Trigger>
          <Combobox.SelectionButton
            layoutClassName="w-full"
            title={displayTitle}
            isPlaceholder={isPlaceholder}
            size="sm"
            endEnhancer={<ChevronDown size={12} className="text-muted" />}
            titleAlignment="start"
          />
        </Combobox.Trigger>
        <Combobox.Popover
          sideOffset={8}
          side="top"
          align="center"
          layoutClassName="w-[160px]"
        >
          <Combobox.Content
            title="Shadow"
            areOptionsSearchable={false}
            emptySearchMessage="No matches found"
          />
        </Combobox.Popover>
      </Combobox.Root>
    </div>
  );
};

export default ShadowModifierComponent;
